// The supported time range (decided in round 2). The compiled tables are exact from 1970-01-01 and their recurring
// rules are proven against ICU until 2100-01-01 (scripts/gen-tz.ts). Outside that window the offsets would be guesses:
// before 1970 the table falls back to the 1970 offset, after 2099 the rules are unverified. So core supports business
// dates 1970-01-02 .. 2099-12-29 (each starts and ends inside the window for any start time and compiled zone, all of
// which are 3.5 to 10 h behind UTC) and instants from 1970-01-04T00:00Z up to 2099-12-30T00:00Z (whose business dates
// are all supported). The business-day functions assume their input is in range;
// boundaries (command decoding, imports, report pickers) validate with these and get an error value, never a guess.
import { formatLocalDate, localDateToDays, MS_PER_DAY } from './calendar.js';
import { toEpochMs } from './zone.js';
import { TZ_EXPLICIT_FROM_SEC, TZ_VERIFIED_UNTIL_SEC } from './tzdata/zones.generated.js';
import { err, ok, type BusinessDate, type InstantInput, type Result } from './types.js';

const FROM_DAYS = TZ_EXPLICIT_FROM_SEC / 86_400 + 1; // first supported business date
const UNTIL_DAYS = TZ_VERIFIED_UNTIL_SEC / 86_400 - 2; // first unsupported business date

/** Supported business dates, inclusive. */
export const SUPPORTED_BUSINESS_DATES: { readonly first: BusinessDate; readonly last: BusinessDate } = {
  first: formatLocalDate(FROM_DAYS),
  last: formatLocalDate(UNTIL_DAYS - 1),
};

/** Supported instants, epoch ms, half-open [from, until). */
export const SUPPORTED_INSTANTS: { readonly from: bigint; readonly until: bigint } = {
  from: BigInt((FROM_DAYS + 2) * MS_PER_DAY), // a 23:45 start can put 1970-01-03 evening local on 01-02
  until: BigInt(UNTIL_DAYS * MS_PER_DAY),
};

export function validateInstant(at: InstantInput): Result<bigint, 'instant_out_of_supported_range'> {
  const ms = BigInt(toEpochMs(at));
  return ms >= SUPPORTED_INSTANTS.from && ms < SUPPORTED_INSTANTS.until ? ok(ms) : err('instant_out_of_supported_range');
}

export function validateBusinessDate(date: BusinessDate): Result<BusinessDate, 'date_out_of_supported_range'> {
  const days = localDateToDays(date);
  return days >= FROM_DAYS && days < UNTIL_DAYS ? ok(date) : err('date_out_of_supported_range');
}
